import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    Switch,
    MenuItem,
    FormControlLabel,
    Checkbox,
    IconButton,
    Tooltip,
    Alert,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
// import {
//     doc,
//     getDoc,
//     addDoc,
//     collection,
//     query,
//     setDoc,
//     updateDoc,
//     where,
//     getDocs
// } from 'firebase/firestore';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';


const FEEDBACK_TYPES = [
    { value: 'rating', label: 'Rating Scale' },
    { value: 'text', label: 'Text Feedback' },
    { value: 'nps', label: 'NPS (0-10)' },
];

export default function FeedbackFormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [isPublished, setIsPublished] = useState(false);
    const [responses, setResponses] = useState({});
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [existingSubmission, setExistingSubmission] = useState(null);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');
    const [jsonText, setJsonText] = useState('');



    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
    const [status, setStatus] = useState('inactive');

    const [newQuestion, setNewQuestion] = useState({
        text: '',
        type: 'rating',
        required: false,
        scale: 5,
    });

    useEffect(() => {
        if (formId && formId !== 'new') {
            fetchForm();
        }
    }, [formId]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const data = formDoc.data();
                setFormTitle(data.title);
                setFormDescription(data.description);
                setQuestions(data.questions || []);
                setStatus(data.status);
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form');
        } finally {
            setLoading(false);
        }
    };
    // const checkIfAlreadySubmitted = async () => {
    //     try {
    //         const q = query(
    //             collection(db, 'submissions'),
    //             where('formId', '==', formId),
    //             where('userId', '==', user.uid)
    //         );

    //         const snapshot = await getDocs(q);

    //         if (!snapshot.empty) {
    //             setAlreadySubmitted(true);
    //         }
    //     } catch (err) {
    //         console.error('Error checking submission:', err);
    //     }
    // };

    const handleSaveForm = async () => {
        try {
            if (!formTitle.trim()) {
                setError('Form title is required');
                return;
            }

            if (questions.length === 0) {
                setError('Add at least one question');
                return;
            }

            setLoading(true);

            const formData = {
                title: formTitle,
                description: formDescription,
                formType: 'feedback',
                questions: questions,
                status: status,
                isPublished: isPublished,
                isLocked: false,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            if (formId && formId !== 'new') {
                await updateDoc(doc(db, 'forms', formId), formData);
            } else {
                await setDoc(doc(db, 'forms', `feedback_${Date.now()}`), formData);
            }

            setError('');
            navigate('/form-builder');
        } catch (err) {
            console.error('Error saving form:', err);
            setError('Failed to save form');
        } finally {
            setLoading(false);
        }
    };

    const handleAddQuestion = () => {
        if (editingQuestionIndex !== null) {
            const updated = [...questions];
            updated[editingQuestionIndex] = newQuestion;
            setQuestions(updated);
        } else {
            setQuestions([...questions, newQuestion]);
        }

        setNewQuestion({
            text: '',
            type: 'rating',
            required: false,
            scale: 5,
        });
        setEditingQuestionIndex(null);
        setDialogOpen(false);
    };

    const handleEditQuestion = (index) => {
        setNewQuestion(questions[index]);
        setEditingQuestionIndex(index);
        setDialogOpen(true);
    };

    const handleDeleteQuestion = (index) => {
        if (window.confirm('Delete this question?')) {
            setQuestions(questions.filter((_, i) => i !== index));
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Form Header */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#333' }}>
                    💬 {formId && formId !== 'new' ? 'Edit' : 'Create'} Feedback Form
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Form Title"
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            placeholder="Enter form title"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            placeholder="Enter form description"
                            multiline
                            rows={3}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={isPublished ? 'Published' : 'Unpublished'}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setJsonInput('');
                            setJsonDialogOpen(true);
                        }}
                    >
                        Import JSON
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/form-builder')}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveForm}
                        disabled={loading}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {loading ? 'Saving...' : 'Save Form'}
                    </Button>
                </Box>
            </Paper>

            {/* Questions Section */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                        ❓ Questions ({questions.length})
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setJsonDialogOpen(true)}
                    >
                        JSON
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setNewQuestion({
                                text: '',
                                type: 'rating',
                                required: false,
                                scale: 5,
                            });
                            setEditingQuestionIndex(null);
                            setDialogOpen(true);
                        }}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        Add Question
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {questions.map((question, index) => (
                        <Grid item xs={12} key={index}>
                            <Card sx={{ borderLeft: '4px solid #667eea' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        {index + 1}. {question.text}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={
                                                FEEDBACK_TYPES.find((t) => t.value === question.type)?.label ||
                                                question.type
                                            }
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={question.required ? 'Required' : 'Optional'}
                                            size="small"
                                            color={question.required ? 'warning' : 'default'}
                                        />
                                        {question.type === 'rating' && (
                                            <Chip
                                                label={`Scale: 1-${question.scale}`}
                                                size="small"
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditQuestion(index)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteQuestion(index)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {questions.length === 0 && (
                    <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No questions added yet. Click "Add Question" to get started.
                    </Typography>
                )}
            </Paper>

            {/* Question Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Question Text"
                        value={newQuestion.text}
                        onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                        multiline
                        rows={2}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Feedback Type</InputLabel>
                        <Select
                            value={newQuestion.type}
                            onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                            label="Feedback Type"
                        >
                            {FEEDBACK_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {newQuestion.type === 'rating' && (
                        <FormControl fullWidth>
                            <InputLabel>Rating Scale</InputLabel>
                            <Select
                                value={newQuestion.scale}
                                onChange={(e) =>
                                    setNewQuestion({ ...newQuestion, scale: e.target.value })
                                }
                                label="Rating Scale"
                            >
                                <MenuItem value={3}>1-3 (Excellent/Good/Poor)</MenuItem>
                                <MenuItem value={5}>1-5 (Very Good to Very Bad)</MenuItem>
                                <MenuItem value={10}>1-10 (Detailed Scale)</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={newQuestion.required}
                                onChange={(e) =>
                                    setNewQuestion({ ...newQuestion, required: e.target.checked })
                                }
                            />
                        }
                        label="Required"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleAddQuestion}
                        variant="contained"
                        disabled={!newQuestion.text}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {editingQuestionIndex !== null ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Import Questions from JSON</DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        label="Paste JSON here"
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                    />

                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={() => {
                            try {
                                const parsed = JSON.parse(jsonText);

                                if (!Array.isArray(parsed.questions)) {
                                    setError('JSON must contain questions array');
                                    return;
                                }

                                setQuestions(parsed.questions);
                                setJsonDialogOpen(false);
                                setJsonText('');
                            } catch (err) {
                                setError('Invalid JSON');
                            }
                        }}
                    >
                        Import
                    </Button>
                </DialogContent>
            </Dialog>

        </Container>
    );
}
